<template>
    <h1 class="mb-1.5 mt-14">Let's confirm your phone number</h1>
    <p class="mb-4 text-sm text-ds-primary-500">Enter your phone number below.</p>
    <DsPhoneInput
        v-model="phoneNumber"
        class="my-3 w-full"
        :error="phoneError"
        label="Phone number"
        @validityChanged="handleValidityChange"
    />
    <DsButton
        class="my-10 w-full"
        data-testid="btn-submit-phone"
        :disabled="!isPhoneValid"
        size="lg"
        type="button"
        variant="primary"
        @click="submitPhoneNumber"
    >
        Continue
    </DsButton>
</template>

<script setup lang="ts">
import { useClientApi } from '@/api/client/api-client';
import type { OnFetchErrorContext } from '@vueuse/core';
import DsButton from '@ds/button/DsButton.vue';
import DsPhoneInput from '@ds/form/input/DsPhoneInput/DsPhoneInput.vue';
import { ref } from 'vue';

const emit = defineEmits<{ phoneNumberCollected: [phoneNumber: string, phoneType: string] }>();

const phoneNumber = ref('');
const isPhoneValid = ref(false);
const phoneError = ref('');

const clientApi = useClientApi();

const handleValidityChange = (isValid: boolean) => {
    isPhoneValid.value = isValid;
    if (isValid) {
        phoneError.value = '';
    }
};

async function submitPhoneNumber() {
    phoneError.value = '';

    const { data, error } = await clientApi('/client/login/submitPhoneNumber', {
        onFetchError(ctx: OnFetchErrorContext) {
            /**
             * Here, `onFetchError` is intercepting the response data and setting `error` to `data`
             * because the error messages are found in `context.data`, not `context.error`.
             * */
            const { data } = ctx;
            const phoneErrors = data?.errors?.phone;
            ctx.error = phoneErrors?.[0] || data;

            return ctx;
        },
    })
        .post({
            phone: phoneNumber.value,
        })
        .json();

    if (error.value) {
        phoneError.value = error.value || 'Unable to verify phone number. Please try again.';
    } else {
        if (data.value) {
            emit('phoneNumberCollected', data.value.phoneNumber, data.value.phoneType);
        }
    }
}
</script>
